import { useMemo, useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";
import Toggle from "react-toggle";

import { ListLoader, Icon } from "components/atoms";
import { StatementListItem } from "components/molecules";
import { SendStatementInvoiceModal, ColumnSettingsModal } from "components/organisms";
import { useColumnPreferences } from "hooks/useColumnPreferences";

import {
	getPropertyStatements,
	getPropertyStatementsCount,
	getPropertyStatementsList,
	getPropertyStatementsLoading,
	getPropertyStatementsOnlyActiveStatus,
	getPropertyStatementsTotal,
	setPropertyStatementsActiveStatus,
} from "state/modules/properties";

import { Statement } from "interfaces/statements";
import { IconName } from "interfaces/icons";

import { MAIN_THEME } from "styles/colors";
import { FONT_FAMILY_MEDIUM } from "styles/typography";

const StatementsList = styled(InfiniteScroll as any)`
	display: grid;
	grid-template-columns: 1fr;
	width: 100%;
	gap: 0;
	overflow: hidden !important;
	background: transparent;
`;

const Container = styled.div`
	padding: 15px 30px 40px;
	overflow-y: auto;

	@media (max-width: 768px) {
		padding: 15px 10px 70px;
	}

	@media (max-width: 480px) {
		padding: 15px 5px 70px;
	}
`;

const ScrollWrapper = styled.div`
	width: 100%;
	overflow-x: auto;
	overflow-y: visible;
	border: 1px solid ${MAIN_THEME.BORDER_COLOR};
	border-radius: 12px;
	background: transparent;

	/* Custom scrollbar styling */
	&::-webkit-scrollbar {
		height: 8px;
	}

	&::-webkit-scrollbar-track {
		background: transparent;
	}

	&::-webkit-scrollbar-thumb {
		background: ${MAIN_THEME.DARK_BG_TERTIARY};
		border-radius: 4px;

		&:hover {
			background: ${MAIN_THEME.GREY};
		}
	}
`;

const TableWrapper = styled.div<{ mobileMinWidth: string }>`
	min-width: 1200px;

	@media (max-width: 768px) {
		min-width: ${(props) => props.mobileMinWidth};
	}
`;


const TableHeaderItem = styled.div`
	font-size: 14px;
	text-align: left;
	display: flex;
	justify-content: flex-start;
	align-items: center;
	color: ${MAIN_THEME.TEXT_SECONDARY};
	height: 52px;
	padding: 10px 8px;
	background: transparent;
	font-family: ${FONT_FAMILY_MEDIUM};
	font-weight: 500;
	text-transform: none;
	letter-spacing: 0.3px;
	border-bottom: 1px solid ${MAIN_THEME.BORDER_COLOR};


	@media (max-width: 768px) {
		font-size: 12px;
		height: 36px;
	}
`;

const TableHeader = styled.div<{ columns?: string }>`
	display: grid;
	grid-template-columns: ${(props) => props.columns || "1fr 1fr 1fr 1fr 1fr 1fr 0.8fr"};
	gap: 0;
	margin-bottom: 0;
	overflow: hidden;
	background: transparent;


`;

const EmptyListMessage = styled.p`
	font-family: ${FONT_FAMILY_MEDIUM};
	color: ${MAIN_THEME.TEXT_SECONDARY};
	font-size: 16px;
	background: ${MAIN_THEME.DARK_BG_TERTIARY};
	border: 1px solid ${MAIN_THEME.BORDER_COLOR};
	border-radius: 8px;
	padding: 15px;
	text-align: center;
`;

const LoaderContainer = styled.div`
	padding: 0;
`;

const SettingsButton = styled.button`
	border: none;
	background: transparent;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-left: auto;

	&:hover {
		opacity: 0.8;
	}
`;

const ContentHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 15px;
`;

const ToggleContainer = styled.div`
	display: flex;
	align-items: center;
`;

const ToggleTitle = styled.p`
	margin-right: 15px;
	color: ${MAIN_THEME.TEXT_PRIMARY};
	font-weight: 500;
`;

const DEFAULT_COLUMNS = [
	{ id: 'period', label: 'Period', isVisible: true, width: '1fr' },
	{ id: 'reservations', label: 'Reservations', isVisible: true, width: '1fr' },
	{ id: 'income', label: 'Income', isVisible: true, width: '1fr' },
	{ id: 'expenses', label: 'Expenses', isVisible: true, width: '1fr' },
	{ id: 'paidOut', label: 'Paid Out', isVisible: true, width: '1fr' },
	{ id: 'createdAt', label: 'Created At', isVisible: true, width: '1fr' },
	{ id: 'viewInvoice', label: 'View Invoice', isVisible: true, width: '0.8fr' },
];

interface Props {
	propertyId: string;
}

const PropertyStatementsList = (props: Props): JSX.Element => {
	const [selectedStatement, setSelectedStatement] = useState<Statement | null>(
		null
	);
	const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

	const { propertyId } = props;

	const { columns, toggleColumn } = useColumnPreferences(
		"propertyStatementsList",
		DEFAULT_COLUMNS
	);

	const visibleColumns = useMemo(
		() => columns.filter((col) => col.isVisible),
		[columns]
	);
	const visibleColumnIds = useMemo(
		() => visibleColumns.map((c) => c.id),
		[visibleColumns]
	);
	const gridTemplateColumns = useMemo(() => {
		return visibleColumns.map((c) => c.width).join(" ");
	}, [visibleColumns]);

	const mobileMinWidth = useMemo(() => {
		return `${Math.max(visibleColumns.length * 120, 100)}px`;
	}, [visibleColumns]);

	const isColumnVisible = (id: string) => visibleColumnIds.includes(id);

	const statementsList = useSelector(getPropertyStatementsList);
	const isLoading = useSelector(getPropertyStatementsLoading);
	const statementsCount = useSelector(getPropertyStatementsCount);
	const statementsTotal = useSelector(getPropertyStatementsTotal);
	const onlyActiveStatements = useSelector(
		getPropertyStatementsOnlyActiveStatus
	);

	const dispatch = useDispatch();

	const handleCloseEditModal = () => {
		setSelectedStatement(null);
	};

	const handleEditClaim = (statement: Statement) => {
		setSelectedStatement(statement);
	};

	const hasMore = useMemo(
		() => statementsCount < statementsTotal,
		[statementsCount, statementsTotal]
	);

	const loadMore = () => {
		if (!isLoading) {
			// dispatch();
			// getPropertyClaims({
			// 	propertyId,
			// 	isLoadMore: true,
			// })
		}
	};

	const toggleActive = () => {
		dispatch(setPropertyStatementsActiveStatus(!onlyActiveStatements));
		dispatch(
			getPropertyStatements({
				propertyId,
				isLoadMore: false,
			})
		);
	};

	const renderLoader = () =>
		statementsCount <= 0 && isLoading ? (
			<LoaderContainer>
				<ListLoader />
			</LoaderContainer>
		) : null;

	const renderSendModal = () => {
		if (selectedStatement) {
			return (
				<SendStatementInvoiceModal
					statementDetails={selectedStatement}
					isVisible={selectedStatement !== null}
					close={handleCloseEditModal}
					propertyId={propertyId}
					// onSuccess={}
				/>
			);
		}
	};

	const renderStatementsList = () => {
		if (statementsCount) {
			return (
				<StatementsList
					scrollableTarget="statementsContainer"
					dataLength={statementsCount}
					next={loadMore}
					hasMore={hasMore}
					loader={<ListLoader />}
				>
					{statementsList.map((statement: Statement) => (
						<StatementListItem
							data={statement}
							key={statement.id}
							onSendClick={handleEditClaim}
							visibleColumnIds={visibleColumnIds}
							gridTemplateColumns={gridTemplateColumns}
						/>
					))}
				</StatementsList>
			);
		}

		if (isLoading) {
			return renderLoader();
		}

		return <EmptyListMessage>Empty list</EmptyListMessage>;
	};

	const renderTableHeader = () =>
		statementsCount ? (
			<TableHeader columns={gridTemplateColumns}>
				{isColumnVisible('period') && <TableHeaderItem>Period</TableHeaderItem>}
				{isColumnVisible('reservations') && <TableHeaderItem>Reservations</TableHeaderItem>}
				{isColumnVisible('income') && <TableHeaderItem>Income</TableHeaderItem>}
				{isColumnVisible('expenses') && <TableHeaderItem>Expenses</TableHeaderItem>}
				{isColumnVisible('paidOut') && <TableHeaderItem>Paid Out</TableHeaderItem>}
				{isColumnVisible('createdAt') && <TableHeaderItem>Created At</TableHeaderItem>}
				{isColumnVisible('viewInvoice') && <TableHeaderItem>View Invoice</TableHeaderItem>}
			</TableHeader>
		) : null;

	return (
		<Container>
			<ContentHeader>
				<ToggleContainer>
					<ToggleTitle>Show Only Active Statements</ToggleTitle>
					<Toggle checked={onlyActiveStatements} onChange={toggleActive} />
				</ToggleContainer>
				<SettingsButton onClick={() => setIsSettingsModalOpen(true)}>
					<Icon name={IconName.SETTINGS} color={MAIN_THEME.TEXT_PRIMARY} size={20} />
				</SettingsButton>
			</ContentHeader>
			{renderSendModal()}
			<ScrollWrapper>
				<TableWrapper mobileMinWidth={mobileMinWidth}>
					{renderTableHeader()}
					{renderStatementsList()}
				</TableWrapper>
			</ScrollWrapper>
			<ColumnSettingsModal
				isVisible={isSettingsModalOpen}
				close={() => setIsSettingsModalOpen(false)}
				columns={columns}
				onToggle={toggleColumn}
			/>
		</Container>
	);
};

export default PropertyStatementsList;
